// @ts-strict-ignore
import { useMemo, useState } from 'react'
import { useAtom, useSetAtom } from 'jotai'
import { SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@node-space/hooks'
import { logSentryErrorAndGetTraceId } from '@node-space/utils'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { usePostConvertQuoteMutation } from 'hooks/mutations/usePostConvertQuoteMutation'
import { usePostQuoteEstimateMutation } from 'hooks/mutations/usePostQuoteEstimateMutation'
import { useValidateReferenceWithdrawalMutation } from 'hooks/mutations/useValidateReferenceWithdrawalMutation'
import { useWalletLimits } from 'hooks/queries/useWalletLimits'
import { useWalletsQueryV2 } from 'hooks/queries/useWalletsQueryV2'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useSupportedCurrencies } from 'hooks/useSupportedCurrencies'
import { BaseErrorResponse } from 'types/beneficiaries'
import { ConvertWallet } from 'types/types'
import { ConvertStep } from 'types/walletActions'
import { quoteEstimate } from 'types/wallets'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { handleOnMutationError } from 'utils/reactQuery/onMutationError'
import track from 'utils/tracker'
import { floorWithPrecision, formatString } from 'utils/utils'
import {
  convertFormValuesAtom,
  convertFromWalletAtom,
  convertStepAtom,
  convertToWalletAtom,
  quoteAtom,
  quoteEstimateAtom,
} from '../atoms/atoms'
import { ConvertDetailsForm } from '../schemas/convertDetailsSchema'
import { getQuotePayload } from '../utils'
import { useConvertDetailsForm } from './useConvertDetailsForm'
import { useConvertEffects } from './useConvertEffects'

export const useConvertDetails = (convertFromCustomerWallet?: ConvertWallet) => {
  const { t } = useTranslation()
  const { getSupportedCurrencyWallets } = useSupportedCurrencies()
  const setQuote = useSetAtom(quoteAtom)
  const setConvertStep = useSetAtom(convertStepAtom)
  const [convertFormValues, setConvertFormValues] = useAtom(convertFormValuesAtom)
  const [quoteEstimate, setQuoteEstimate] = useAtom(quoteEstimateAtom)
  const [convertToWallet, setConvertToWallet] = useAtom(convertToWalletAtom)
  const [convertFromWallet, setConvertFromWallet] = useAtom(convertFromWalletAtom)

  const amplitudeEventOptions = {
    category: AmplitudeEventCategory.MERCHANT_PORTAL,
    action: AmplitudeEventAction.CLICK,
    estimate: true,
    fromCurrency: convertFromWallet?.currency?.code?.toLowerCase(),
    toCurrency: convertToWallet?.currency?.code?.toLowerCase(),
  }

  const [searchWalletQuery, setSearchWalletQuery] = useState('')

  const debouncedSearchWalletTerm = useDebounce(searchWalletQuery, SEARCH_INPUT_DEBOUNCE_TIME)

  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { form, formValues, errors, isSubmitting, formFields, handleSubmit, setValue } =
    useConvertDetailsForm()

  // API Requests
  const { data: wallets, isFetching: isFetchingWallets } = useWalletsQueryV2({
    walletType: '',
    searchTerm: debouncedSearchWalletTerm,
    max: 100,
    order: 'asc',
    sort: 'description',
  })

  const {
    data: withdrawalLimits,
    isFetching: isFetchingLimits,
    isError: isErrorLimits,
    error: ErrorLimits,
  } = useWalletLimits(
    convertFromWallet?.currency?.code,
    (convertFromWallet?.id || convertFromWallet?.lsid) as number,
    !convertFromCustomerWallet && !!convertFromWallet?.currency?.code && !!convertFromWallet?.id
  )

  const { mutate: postQuoteEstimate, isPending: isPostingQuoteEstimate } =
    usePostQuoteEstimateMutation()

  const { mutate: convertQuote, isPending: isConvertingQuote } = usePostConvertQuoteMutation()

  const { mutate: mutateReference, isPending: isValidatingReference } =
    useValidateReferenceWithdrawalMutation()

  // Derived values
  const exchangeRate = quoteEstimate?.amountOut / (quoteEstimate?.amountIn || 1)
  const limitMinimum = withdrawalLimits?.minimum
  const isSameCurrency = convertFromWallet?.currency?.code === convertToWallet?.currency?.code

  const adjustedMin = isSameCurrency
    ? limitMinimum
    : floorWithPrecision(limitMinimum, convertFromWallet?.currency?.pricePrecision)

  const minimumAmount = adjustedMin
    ? formatString(
        t('minimumAmount'),
        floorWithPrecision(adjustedMin, convertFromWallet?.currency?.pricePrecision),
        convertFromWallet?.currency?.code
      )
    : ''

  const activeWallets = getSupportedCurrencyWallets(wallets?.data)

  const sourceWallets = useMemo(
    () => activeWallets?.filter(wallet => wallet?.balance > 0),
    [activeWallets]
  )

  const sourceToWallets = useMemo(
    () => activeWallets?.filter(wallet => wallet?.lsid !== convertFromWallet?.lsid),
    [activeWallets, convertFromWallet]
  )

  const isDisabledRequestQuote =
    !!Object.keys(errors)?.length || isPostingQuoteEstimate || isConvertingQuote
  const isLoadingRequestQuote =
    isPostingQuoteEstimate || isConvertingQuote || isValidatingReference || isSubmitting

  // Helper functions
  const handleFetchQuoteEstimate = () => {
    resetRequestError()

    if (isSameCurrency) return

    const data: quoteEstimate = {
      from: convertFromWallet?.currency?.code,
      to: convertToWallet?.currency?.code,
    }
    postQuoteEstimate(data, {
      onSuccess: quoteEstimate => {
        track.Amp.track(AmplitudeEvent.CONVERT_REQUEST_QUOTE, {
          ...amplitudeEventOptions,
          estimate: true,
        })
        setQuoteEstimate(quoteEstimate)
      },
      onError: (error: BaseErrorResponse) => {
        track.Amp.track(AmplitudeEvent.EOF_CONVERT_REQUEST_QUOTE_ERROR, {
          ...amplitudeEventOptions,
          estimate: true,
        })
        setQuoteEstimate(null)
        handleOnMutationError(error, data, setRequestError, '/v1/quote', 'postQuoteEstimate', true)
      },
    })
  }

  const handleReferenceValidate = ({ target }) => {
    form.setValue('reference', target?.value)
    form.clearErrors('reference')

    const data = {
      reference: target?.value,
    }

    mutateReference(data, {
      onSuccess: () => {
        form.clearErrors('reference')
      },
      onError: (error: BaseErrorResponse) => {
        if (error?.status !== 412) {
          logSentryErrorAndGetTraceId(
            `Failed API request to /wallet/withdrawal/request/validate with status ${error?.status} - mutateReference`,
            error,
            {
              data: typeof data === 'string' ? { message: data } : data,
            }
          )
          form.setError('reference', {
            message: t('referenceFailed'),
          })
        } else {
          form.setError('reference', {
            message: error?.errorList[0]?.message,
          })
        }
      },
    })
  }

  const handleOpen = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchWalletQuery('')
    }
  }

  const handleFromWalletOnChange = (walletId: string) => {
    const selectedWallet = sourceWallets?.find(wallet => wallet?.lsid === walletId)

    if (walletId !== convertFromWallet?.lsid) {
      form?.setValue('fromWallet', walletId, {
        shouldValidate: true,
        shouldDirty: true,
      })
      setConvertFromWallet(selectedWallet)
      setConvertToWallet(null)
      form?.setValue('toWallet', '')
      form?.setValue('toAmount', '')
      form?.setValue('fromAmount', '')
    }
    setSearchWalletQuery('')
  }

  const handleToWalletOnChange = (walletId: string) => {
    const selectedWallet = sourceToWallets?.find(wallet => wallet?.lsid === walletId)

    form?.setValue('toWallet', walletId, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setConvertToWallet(selectedWallet)
    setSearchWalletQuery('')
  }

  const onSubmit: SubmitHandler<typeof formValues> = () => {
    const data = getQuotePayload(
      convertFromWallet,
      convertToWallet,
      formValues,
      convertFormValues?.changedField
    )
    if (!data) return
    resetRequestError()

    convertQuote(data, {
      onSuccess: response => {
        track.Amp.track(AmplitudeEvent.CONVERT_REQUEST_QUOTE, amplitudeEventOptions)
        setQuote(response)
        setConvertStep(ConvertStep.REVIEW)
        setConvertFormValues({
          ...(formValues as Required<ConvertDetailsForm>),
          changedField: convertFormValues?.changedField,
        })
      },
      onError: (error: BaseErrorResponse) => {
        track.Amp.track(AmplitudeEvent.EOF_CONVERT_REQUEST_QUOTE_ERROR, amplitudeEventOptions)
        handleOnMutationError(error, data, setRequestError, '/v1/quote', 'convertQuote', true)
      },
    })
  }

  useConvertEffects({
    convertFormValues,
    exchangeRate,
    formValues,
    convertFromCustomerWallet,
    sourceWallets,
    isErrorLimits,
    ErrorLimits,
    setValue,
    setRequestError,
    handleFetchQuoteEstimate,
  })

  return {
    requestError,
    formData: {
      formFields,
      formValues,
      sourceWallets,
      sourceToWallets,
      isSameCurrency,
      minimumAmount,
    },
    formStatus: {
      isDisabledRequestQuote,
      isLoadingRequestQuote,
      isFetchingWallets,
      isFetchingLimits,
      isValidatingReference,
      isPostingQuoteEstimate,
    },
    formhandlers: {
      handleReferenceValidate,
      handleFromWalletOnChange,
      handleToWalletOnChange,
      setSearchWalletQuery,
      handleOpen,
      handleSubmit,
      onSubmit,
      setValue,
    },
  }
}
